// super-excalidraw: thin client for /api/diagrams/<id>/comments. Comments
// are stored server-side per-diagram (one JSON file each). Author is set
// from HTTP basic auth on the server when omitted.

export type CommentDTO = {
  id: string;
  parentId?: string;
  diagramId: string;
  elementId?: string;
  anchorX?: number;
  anchorY?: number;
  body: string;
  author?: string;
  resolved?: boolean;
  createdAt: number;
  updatedAt?: number;
  reactions?: Record<string, string[]>;
};

export type CommentListDTO = {
  items: CommentDTO[];
  updatedAt?: number;
};

const root = (diagramId: string) =>
  `/api/diagrams/${encodeURIComponent(diagramId)}/comments`;

export async function listComments(diagramId: string): Promise<CommentListDTO> {
  const r = await fetch(root(diagramId) + "/");
  if (!r.ok) {
    throw new Error(`comments list ${r.status}`);
  }
  return r.json();
}

export async function createComment(
  diagramId: string,
  payload: Partial<CommentDTO> & { body: string },
): Promise<CommentDTO> {
  const r = await fetch(root(diagramId) + "/", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
  if (!r.ok) {
    throw new Error(`comments create ${r.status}`);
  }
  return r.json();
}

export async function patchComment(
  diagramId: string,
  id: string,
  patch: {
    body?: string;
    resolved?: boolean;
    reactions?: Record<string, string[]>;
  },
): Promise<CommentDTO> {
  const r = await fetch(`${root(diagramId)}/${encodeURIComponent(id)}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(patch),
  });
  if (!r.ok) {
    throw new Error(`comments patch ${r.status}`);
  }
  return r.json();
}

export async function deleteComment(
  diagramId: string,
  id: string,
): Promise<void> {
  const r = await fetch(`${root(diagramId)}/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
  if (!r.ok) {
    throw new Error(`comments delete ${r.status}`);
  }
}

// Build/parse a permalink fragment that opens a specific comment when the
// diagram loads: `#json=<id>,<key>&comment=<commentId>`.
export const buildCommentPermalink = (
  diagramId: string,
  key: string,
  commentId: string,
): string =>
  `${window.location.origin}/#json=${encodeURIComponent(
    diagramId,
  )},${encodeURIComponent(key)}&comment=${encodeURIComponent(commentId)}`;

export const parseCommentFromHash = (): string | null => {
  const m = window.location.hash.match(/[#&?]comment=([^&]+)/);
  return m ? decodeURIComponent(m[1]) : null;
};

export const formatRelativeTime = (epochSeconds: number): string => {
  const diff = Math.floor(Date.now() / 1000) - epochSeconds;
  if (diff < 60) {
    return "just now";
  }
  if (diff < 3600) {
    return `${Math.floor(diff / 60)} min ago`;
  }
  if (diff < 86400) {
    return `${Math.floor(diff / 3600)} h ago`;
  }
  if (diff < 7 * 86400) {
    return `${Math.floor(diff / 86400)} d ago`;
  }
  return new Date(epochSeconds * 1000).toLocaleDateString();
};

export const initialOf = (name: string | undefined): string => {
  if (!name) {
    return "?";
  }
  const trimmed = name.trim();
  if (!trimmed) {
    return "?";
  }
  return trimmed[0].toUpperCase();
};
